import React, { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import { DeleteOutline, Edit, PlayCircleOutline, Search } from "@material-ui/icons";
import { fade, makeStyles } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import AgentTemplateLibrary from "../../components/AgentTemplateLibrary";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import ForbiddenPage from "../../components/ForbiddenPage";
import MetricsStrip from "../../components/MetricsStrip";
import AgentPreviewPanel from "../../components/AgentPreviewPanel";
import CompanyPromptValues from "../../components/CompanyPromptValues";
import AiCredentialsModal from "../../components/AiCredentialsModal";

const useStyles = makeStyles((theme) => ({
  mainContainer: { minWidth: 0 },
  stack: { display: "flex", flexDirection: "column", gap: theme.spacing(1.5), minWidth: 0 },
  hero: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: { alignItems: "stretch", flexDirection: "column" },
  },
  heroTitle: { fontWeight: 600, letterSpacing: "-0.01em" },
  heroActions: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap" },
  tabsPaper: {
    padding: theme.spacing(0.75),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tabs: { minHeight: 0 },
  tab: {
    minHeight: 40,
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 600,
    "&.Mui-selected": { backgroundColor: theme.palette.action.hover },
  },
  searchPaper: {
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableContainer: { maxHeight: "64vh", ...theme.scrollbarStyles },
  tableHead: { fontWeight: 700 },
  agentName: { fontWeight: 600 },
  preview: { maxWidth: 310, opacity: 0.72 },
  origin: { display: "flex", flexDirection: "column", gap: theme.spacing(0.5), alignItems: "flex-start" },
  connections: { display: "flex", gap: theme.spacing(0.5), justifyContent: "center", flexWrap: "wrap" },
  updateChip: { backgroundColor: fade(theme.palette.warning.light, 0.2) },
  empty: { padding: theme.spacing(5, 2), textAlign: "center" },
}));

const Prompts = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const history = useHistory();
  const companyId = user.companyId;

  const [section, setSection] = useState(() =>
    ["library", "values"].includes(new URLSearchParams(window.location.search).get("section"))
      ? new URLSearchParams(window.location.search).get("section") : "agents"
  );
  const [prompts, setPrompts] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchParam, setSearchParam] = useState("");
  const [loading, setLoading] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);
  const [deletePrompt, setDeletePrompt] = useState(null);
  const [previewPrompt, setPreviewPrompt] = useState(null);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [credentialDefaults, setCredentialDefaults] = useState({ purpose: "chat" });

  useEffect(() => {
    const checkPlan = async () => {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useOpenAi) {
        toast.error("Esta empresa não possui permissão para acessar esta página.");
        setTimeout(() => history.push("/"), 1000);
      }
    };
    checkPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (section !== "agents") return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/prompt", {
          params: {
            searchParam,
            pageNumber: page + 1,
            pageSize: rowsPerPage,
          },
        });
        if (active) {
          setPrompts(data.prompts || []);
          setCount(data.count || 0);
        }
      } catch (error) {
        if (active) toastError(error);
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [page, reloadFlag, rowsPerPage, searchParam, section]);

  useEffect(() => {
    const refresh = () => setReloadFlag((current) => current + 1);
    socket.on(`company-${companyId}-prompt`, refresh);
    return () => socket.off(`company-${companyId}-prompt`, refresh);
  }, [companyId, socket]);

  const summary = useMemo(() => ({
    total: count,
    fromTemplate: prompts.filter((prompt) => prompt.origin).length,
    connected: prompts.filter((prompt) => prompt.bindings?.total).length,
    updates: prompts.filter((prompt) => prompt.origin?.updateAvailable).length,
  }), [count, prompts]);

  const metrics = [
    { key: "total", label: "Meus agentes", value: summary.total },
    { key: "templates", label: "Da biblioteca nesta página", value: summary.fromTemplate },
    { key: "connections", label: "Com vínculo nesta página", value: summary.connected },
    { key: "updates", label: "Com revisão mais recente", value: summary.updates },
  ];

  const closeCredentialsModal = () => {
    setCredentialsModalOpen(false);
  };

  const openTemplate = (template) => {
    history.push(`/prompts/new?template=${encodeURIComponent(template.slug)}`);
  };

  const editPrompt = (prompt) => {
    history.push(`/prompts/${prompt.id}/edit`);
  };

  const removePrompt = async () => {
    if (!deletePrompt) return;
    try {
      await api.delete(`/prompt/${deletePrompt.id}`);
      toast.success("Agente removido.");
      setReloadFlag((current) => current + 1);
    } catch (error) {
      toastError(error);
    } finally {
      setDeletePrompt(null);
    }
  };

  if (user.profile === "user") {
    return <MainContainer className={classes.mainContainer} fullWidth><ForbiddenPage /></MainContainer>;
  }

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <ConfirmationModal
        title={deletePrompt ? `Excluir ${deletePrompt.name}?` : "Excluir agente"}
        open={Boolean(deletePrompt)}
        onClose={() => setDeletePrompt(null)}
        onConfirm={removePrompt}
      >
        O agente só pode ser excluído quando não estiver vinculado a uma conexão ou fluxo.
      </ConfirmationModal>

      <AgentPreviewPanel
        open={Boolean(previewPrompt)}
        prompt={previewPrompt}
        onClose={() => setPreviewPrompt(null)}
      />

      <AiCredentialsModal
        open={credentialsModalOpen}
        onClose={closeCredentialsModal}
        onSaved={() => setReloadFlag((current) => current + 1)}
        initialPurpose={credentialDefaults.purpose}
        initialProvider={credentialDefaults.provider}
      />

      <div className={classes.stack}>
        <div className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>Agentes de IA</Typography>
            <Typography variant="body2" color="textSecondary">
              Crie do zero ou use uma configuração guiada da biblioteca Fluyra.
            </Typography>
          </Box>
          <div className={classes.heroActions}>
            <Button variant="outlined" onClick={() => {
              setCredentialDefaults({ purpose: "chat" });
              setCredentialsModalOpen(true);
            }}>
              Credenciais de IA
            </Button>
            {section === "agents" && (
              <>
              <Button variant="outlined" onClick={() => setReloadFlag((value) => value + 1)} disabled={loading}>
                Atualizar
              </Button>
              <Button variant="contained" color="primary" onClick={() => history.push("/prompts/new")}>
                Criar do zero
              </Button>
              </>
            )}
          </div>
        </div>

        <Paper className={classes.tabsPaper}>
          <Tabs value={section} onChange={(_event, value) => value === "knowledge" ? history.push("/knowledge-bases") : value === "usage" ? history.push("/ai-usage") : setSection(value)} indicatorColor="primary" variant="scrollable" scrollButtons="auto">
            <Tab className={classes.tab} value="agents" label="Meus agentes" />
            <Tab className={classes.tab} value="library" label="Biblioteca de templates" />
            <Tab className={classes.tab} value="values" label="Variáveis da empresa" />
            <Tab className={classes.tab} value="knowledge" label="Bases de conhecimento" />
            <Tab className={classes.tab} value="usage" label="Consumo e saúde" />
          </Tabs>
        </Paper>

        {section === "values" ? <CompanyPromptValues /> : section === "library" ? (
          <AgentTemplateLibrary onUse={openTemplate} />
        ) : (
          <>
            <MetricsStrip ariaLabel="Indicadores de agentes" items={metrics} />
            <Paper className={classes.searchPaper}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Buscar em todos os agentes por nome, prompt, fila ou modelo"
                value={searchParam}
                onChange={(event) => { setSearchParam(event.target.value); setPage(0); }}
                inputProps={{ "aria-label": "Pesquisar agentes" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search color="action" /></InputAdornment>,
                }}
              />
            </Paper>

            <Paper className={classes.tablePaper}>
              <TableContainer className={classes.tableContainer}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    <TableCell className={classes.tableHead}>Agente</TableCell>
                    <TableCell className={classes.tableHead}>Origem</TableCell>
                    <TableCell align="center" className={classes.tableHead}>Fila</TableCell>
                    <TableCell align="center" className={classes.tableHead}>Modelo</TableCell>
                    <TableCell align="center" className={classes.tableHead}>Vínculos</TableCell>
                    <TableCell align="center" className={classes.tableHead}>Ações</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {prompts.map((prompt) => (
                      <TableRow key={prompt.id} hover>
                        <TableCell>
                          <Typography variant="body2" className={classes.agentName}>{prompt.name}</Typography>
                          <Typography variant="body2" noWrap className={classes.preview}>{prompt.prompt}</Typography>
                          <Box mt={0.5}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={prompt.knowledge?.mode === "disabled"
                                ? "Conhecimento desativado"
                                : `${prompt.knowledge?.bases?.length || 0} base(s) · ${prompt.knowledge?.mode === "required" ? "obrigatório" : "opcional"}`}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <div className={classes.origin}>
                            {prompt.origin ? <>
                              <Chip size="small" color="primary"
                                label={`${prompt.origin.slug} · v${prompt.origin.version}`} />
                              {prompt.origin.updateAvailable && (
                                <Chip size="small" className={classes.updateChip}
                                  label={`v${prompt.origin.latestVersion} disponível`} />
                              )}
                              {prompt.origin.templateStatus === "archived" && (
                                <Chip size="small" variant="outlined" label="Template arquivado" />
                              )}
                            </> : <Chip size="small" variant="outlined" label="Criado do zero" />}
                          </div>
                        </TableCell>
                        <TableCell align="center">{prompt.queue?.name || "—"}</TableCell>
                        <TableCell align="center">{prompt.model || "—"}</TableCell>
                        <TableCell align="center">
                          <div className={classes.connections}>
                            {prompt.connections?.map((connection) => (
                              <Chip key={`connection-${connection.id}`} size="small" label={`Conexão: ${connection.name}`} />
                            ))}
                            {prompt.flows?.map((flow) => (
                              <Chip key={`flow-${flow.id}-${flow.nodeId}`} size="small" label={`Fluxo: ${flow.name}`} />
                            ))}
                            {prompt.queues?.map((queue) => (
                              <Chip key={`queue-${queue.id}`} size="small" label={`Fila: ${queue.name}`} />
                            ))}
                            {prompt.activeTickets?.length > 0 && (
                              <Chip size="small" label={`${prompt.activeTickets.length} atendimento(s) ativo(s)`} />
                            )}
                            {!prompt.bindings?.total && (
                              <Chip size="small" variant="outlined" label="Sem vínculo" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" aria-label={`Testar ${prompt.name}`} onClick={() => setPreviewPrompt(prompt)}>
                            <PlayCircleOutline />
                          </IconButton>
                          <IconButton size="small" aria-label={`Editar ${prompt.name}`} onClick={() => editPrompt(prompt)}>
                            <Edit />
                          </IconButton>
                          <IconButton size="small" aria-label={`Excluir ${prompt.name}`}
                            disabled={Boolean(prompt.bindings?.total)}
                            onClick={() => setDeletePrompt(prompt)}>
                            <DeleteOutline />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {loading && <TableRowSkeleton columns={6} />}
                  </TableBody>
                </Table>
              </TableContainer>
              {!loading && prompts.length === 0 && (
                <Box className={classes.empty}>
                  <Typography variant="h6">Nenhum agente encontrado</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Ajuste a busca, crie do zero ou escolha um template na biblioteca.
                  </Typography>
                </Box>
              )}
              <TablePagination
                component="div"
                count={count}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_event, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
                rowsPerPageOptions={[20, 40, 50]}
                labelRowsPerPage="Por página"
              />
            </Paper>
          </>
        )}
      </div>
    </MainContainer>
  );
};

export default Prompts;
